import { LocalizationString } from '@celito.clients/assets';
import { dismissToast } from '@celito.clients/provider';
import { errorToast, infoToast } from '@celito.clients/utils';
import { isArray, isArrayBuffer } from 'lodash';

type DownloadableData = ArrayBuffer | BlobPart;

class FilesService {
  private approvalPageData: {
    buffer: string;
    label: string;
  } | null = null;

  /**
   * Downloads a file buffer.
   *
   * @param data - The file data as an ArrayBuffer or BlobPart.
   * @param label - The label or name of the file.
   * @param mimeType - The optional MIME type of the file.
   */
  public downloadFileBuffer({
    data,
    label,
    mimeType,
  }: {
    data: DownloadableData;
    label: string;
    mimeType?: string;
  }): void {
    const blob = this.getBlobFromData(data, mimeType);
    const downloadLink = document?.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = label;
    downloadLink.click();
    URL.revokeObjectURL(downloadLink.href);
  }

  /**
   * Raises a toast indicating that a file is being downloaded and returns the result of the download promise.
   *
   * The toast is dismissed when the promise is resolved.
   *
   * Also handles errors and displays an error toast if the promise is rejected.
   *
   * Usage:
   * Pass a promise that resolves to the file data and an optional callback to handle the result.
   *
   * @param downloadPromise - The promise that resolves to the file data.
   * @param onSuccesfulDownload - Optional callback function to handle the result of the download.
   * @param onError - Optional callback function to handle errors.
   * @returns A promise that resolves to the result of the download.
   */
  public async downloadFile<T>(
    downloadPromise: Promise<T>,
    onSuccesfulDownload?: (result: T) => void,
    onError?: (error: unknown) => void,
    toastOptions?: { message: string; title?: string }
  ): Promise<T | undefined> {
    const toastId = infoToast(
      toastOptions ?? {
        message: LocalizationString.THIS_MAY_TAKE_A_FEW_MOMENTS,
        title: LocalizationString.DOWNLOADING_FILE,
      },
      { timeout: -1 }
    );
    try {
      const result = await downloadPromise;

      const isApprovalPage =
        result &&
        typeof result === 'object' &&
        'approverPageBuffer' in result &&
        'label' in result &&
        typeof result.approverPageBuffer === 'string' &&
        result.approverPageBuffer && // Make sure it's not empty
        typeof result.label === 'string';

      if (isApprovalPage) {
        this.approvalPageData = {
          buffer: result.approverPageBuffer as string,
          label: result.label as string,
        };
      }

      onSuccesfulDownload?.(result);

      return result;
    } catch (error) {
      errorToast({
        message: LocalizationString.THERE_WAS_SOME_PROBLEM_DOWNLOADING_THE_FILE,
      });
      onError?.(error);
    } finally {
      // Only attempt to download approval page if we have valid data
      if (this.approvalPageData?.buffer && this.approvalPageData?.label) {
        // Add a delay to prevent browser download conflicts
        setTimeout(() => {
          this.downloadApprovalPage(
            this.approvalPageData?.buffer as string,
            this.approvalPageData?.label as string
          );
          // Reset approval page data after download
          this.approvalPageData = null;
        }, 500);
      }

      dismissToast(toastId);
    }
  }

  private getBlobFromData(data: DownloadableData, mimeType?: string): Blob {
    return new Blob(
      [isArrayBuffer(data) || isArray(data) ? new Uint8Array(data) : data],
      { type: mimeType }
    );
  }

  private downloadApprovalPage(
    approvalPageBuffer: string,
    label: string
  ): void {
    try {
      const binaryString = atob(approvalPageBuffer);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Use the Uint8Array data
      if (bytes.length > 0) {
        this.downloadFileBuffer({
          data: bytes.buffer,
          label: `approval_page_for_${label}.pdf`,
          mimeType: 'application/pdf',
        });
      }
    } catch (decodeError) {
      console.error('Failed to decode approval page data:', decodeError);
    }
  }
}
/**
 * Service for handling file buffers and downloads.
 */
export const files = new FilesService();
